import { Box } from '@superdispatch/ui-lab';
import { useCallback, useEffect } from 'react';
import { useMatch } from 'react-router-dom';
import { FormikDrawer } from 'shared/form/FormikDrawer';
import { SuperPayBankAccountDTO } from 'shared/modules/superpay/SuperPayDTO';
import { bypassNavigationPrompt, Prompt } from 'shared/routing/NavigationBlock';
import { SUPERPAY_PROFILE_ROUTES } from '../SuperPayRoute';
import { useTrackChangeBankAccountEvent } from './ChangeBankAccountAnalytics';
import { ChangeBankAccountBankInfoStep } from './ChangeBankAccountBankInfoStep';
import { ChangeBankAccountConfirmStep } from './ChangeBankAccountConfirmStep';
import { ChangeBankAccountVerifyStep } from './ChangeBankAccountVerifyStep';
import { useChangeBankAccountFlow } from './useChangeBankAccountFlow';

const UNSAVED_CHANGES_MESSAGE =
  'Changes have not been saved. Leaving can result in unsaved changes being lost.';

interface Props {
  bankAccount?: SuperPayBankAccountDTO;
  onClose: () => void;
  onComplete: () => void;
}

export function ChangeBankAccountDrawer({
  bankAccount,
  onClose,
  onComplete,
}: Props) {
  const isOpen = !!useMatch(SUPERPAY_PROFILE_ROUTES.changeBankAccount);
  const trackChangeBankAccountEvent = useTrackChangeBankAccountEvent();
  const {
    step,
    submittedBankAccount,
    stepState,
    setStepState,
    handleContinue,
    handleConfirmed,
    reset,
  } = useChangeBankAccountFlow({ onComplete });

  useEffect(() => {
    if (isOpen) {
      trackChangeBankAccountEvent('Carrier Opened Change Bank Account Slider');
    }
  }, [isOpen, trackChangeBankAccountEvent]);

  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  const handleClose = useCallback(() => {
    if (stepState.isSubmitting) return;
    if (
      stepState.hasUnsavedChanges &&
      // eslint-disable-next-line no-alert
      !window.confirm(UNSAVED_CHANGES_MESSAGE)
    ) {
      return;
    }
    bypassNavigationPrompt(onClose);
  }, [onClose, stepState.hasUnsavedChanges, stepState.isSubmitting]);

  const handleForceClose = useCallback(() => {
    bypassNavigationPrompt(onClose);
  }, [onClose]);

  return (
    <FormikDrawer open={isOpen} onClose={handleClose}>
      <Prompt
        when={stepState.hasUnsavedChanges && !stepState.isSubmitting}
        message={UNSAVED_CHANGES_MESSAGE}
      />
      <Box maxWidth={['initial', 'initial', '432px']}>
        {step === 'verify' ? (
          <ChangeBankAccountVerifyStep
            bankAccount={bankAccount}
            onClose={handleClose}
            onForceClose={handleForceClose}
            onComplete={onComplete}
            onStepStateChange={setStepState}
          />
        ) : step === 'confirm' && submittedBankAccount ? (
          <ChangeBankAccountConfirmStep
            bankAccount={submittedBankAccount}
            onClose={handleClose}
            onComplete={handleConfirmed}
            onStepStateChange={setStepState}
          />
        ) : (
          <ChangeBankAccountBankInfoStep
            onClose={handleClose}
            onContinue={handleContinue}
            onStepStateChange={setStepState}
          />
        )}
      </Box>
    </FormikDrawer>
  );
}
